/**
 * ScheduleReview Component
 *
 * Admin review of pending_approval schedules across all contractors.
 * Shows expandable weekly grid, approve/reject with notes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContractorSchedule, ScheduleBlock, ScheduleBlockType } from '@/types/contractors';
import { DAY_NAMES } from '@/types/contractors';
import { schedulesApi } from '@/services/contractorsApi';

const BLOCK_TYPE_COLORS: Record<ScheduleBlockType, string> = {
  work: 'bg-blue-100 text-blue-800',
  meeting: 'bg-purple-100 text-purple-800',
  admin: 'bg-yellow-100 text-yellow-800',
  other: 'bg-gray-100 text-gray-800',
};

export function ScheduleReview(): React.JSX.Element {
  const { t } = useTranslation('contractors');

  const [schedules, setSchedules] = useState<ContractorSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await schedulesApi.list({ status: 'pending_approval', limit: 100 });
      setSchedules(Array.isArray(result) ? result : []);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchSchedules();
  }, [fetchSchedules]);

  const handleToggleExpand = async (scheduleId: string): Promise<void> => {
    if (expandedId === scheduleId) {
      setExpandedId(null);
      return;
    }
    // Fetch full schedule with blocks
    try {
      const full = await schedulesApi.get(scheduleId);
      setSchedules((prev) =>
        prev.map((s) => (s.id === scheduleId ? full : s))
      );
      setExpandedId(scheduleId);
    } catch {
      setError(t('errors.loadFailed'));
    }
  };

  const handleReview = async (scheduleId: string, action: 'approve' | 'reject'): Promise<void> => {
    setProcessing(scheduleId);
    setError(null);
    try {
      await schedulesApi.review(scheduleId, {
        action,
        notes: reviewNotes[scheduleId] || undefined,
      });
      await fetchSchedules();
      setExpandedId(null);
    } catch {
      setError(t('errors.reviewFailed'));
    } finally {
      setProcessing(null);
    }
  };

  const renderWeekGrid = (blocks: ScheduleBlock[]): React.JSX.Element => {
    const blocksByDay = new Map<number, ScheduleBlock[]>();
    for (let i = 0; i < 7; i++) blocksByDay.set(i, []);
    for (const block of blocks) {
      const dayBlocks = blocksByDay.get(block.day_of_week) ?? [];
      dayBlocks.push(block);
      blocksByDay.set(block.day_of_week, dayBlocks);
    }

    return (
      <div className="grid grid-cols-7 gap-1 mt-3">
        {DAY_NAMES.map((day, idx) => (
          <div key={day}>
            <div className="text-xs font-semibold text-gray-500 text-center pb-1">{day.slice(0, 3)}</div>
            <div className="space-y-1 min-h-[60px]">
              {(blocksByDay.get(idx) ?? [])
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map((block) => (
                  <div
                    key={block.id}
                    className={`p-1 rounded text-xs ${BLOCK_TYPE_COLORS[block.block_type]}`}
                  >
                    <div className="font-medium">{block.start_time}-{block.end_time}</div>
                    {block.project_name && <div className="truncate opacity-80">{block.project_name}</div>}
                  </div>
                ))}
              {(blocksByDay.get(idx) ?? []).length === 0 && (
                <div className="text-xs text-gray-300 text-center py-2">-</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('scheduleReview.title')}</h2>
        <span className="text-sm text-gray-500">
          {t('scheduleReview.pendingCount', { count: schedules.length })}
        </span>
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {isLoading ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>
      ) : schedules.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">{t('scheduleReview.noPending')}</div>
      ) : (
        <div className="space-y-4">
          {schedules.map((sched) => {
            const isExpanded = expandedId === sched.id;
            const isProcessing = processing === sched.id;
            return (
              <div key={sched.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Schedule header */}
                <div
                  onClick={() => handleToggleExpand(sched.id)}
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                >
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {sched.contractor_name || `Contractor #${sched.contractor_user_id}`}
                    </span>
                    <span className="mx-2 text-gray-300">|</span>
                    <span className="text-sm text-gray-600">{sched.name || t('schedules.untitled')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {sched.effective_from} - {sched.effective_until || t('schedules.ongoing')}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    {/* Weekly grid */}
                    {sched.blocks && sched.blocks.length > 0 ? (
                      renderWeekGrid(sched.blocks)
                    ) : (
                      <p className="text-sm text-gray-400 py-3">{t('scheduleReview.noBlocks')}</p>
                    )}

                    {/* Review notes */}
                    <div className="mt-4">
                      <textarea
                        value={reviewNotes[sched.id] ?? ''}
                        onChange={(e) => setReviewNotes((prev) => ({ ...prev, [sched.id]: e.target.value }))}
                        placeholder={t('scheduleReview.notesPlaceholder')}
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-3">
                      <button
                        onClick={() => handleReview(sched.id, 'reject')}
                        disabled={isProcessing}
                        className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {t('scheduleReview.reject')}
                      </button>
                      <button
                        onClick={() => handleReview(sched.id, 'approve')}
                        disabled={isProcessing}
                        className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {t('scheduleReview.approve')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
